import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Table,
  Button,
  Spinner,
  Form,
  InputGroup,
  Pagination,
} from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import BillModal from "../components/BillModal";
import API_BASE_URL from "../config";

export default function ManageCompanyBookings() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem("selectedCompany");
    return storedCompany ? JSON.parse(storedCompany).id : "";
  });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(() => {
    const storedBranch = localStorage.getItem("selectedBranch");
    return storedBranch ? JSON.parse(storedBranch).id : "";
  });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [showBillModal, setShowBillModal] = useState(false);
  const [billPrefill, setBillPrefill] = useState(null);

  // 🔹 Load companies on mount and validate selectedCompany
  useEffect(() => {
    async function fetchCompanies() {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const companyData = res.data || [];
        setCompanies(companyData);

        // Validate stored selectedCompany
        const storedCompany = localStorage.getItem("selectedCompany");
        if (storedCompany) {
          const parsedCompany = JSON.parse(storedCompany);
          const isValidCompany = companyData.some((c) => c.id === parsedCompany.id);
          if (!isValidCompany) {
            localStorage.removeItem("selectedCompany");
            setSelectedCompany("");
            localStorage.removeItem("selectedBranch");
            setBranchId("");
          }
        }
      } catch (err) {
        console.error("fetchCompanies", err);
        Swal.fire("Error", "Failed to load companies", "error");
        setSelectedCompany("");
        setBranchId("");
      }
    }
    fetchCompanies();
  }, []);

  // 🔹 Load branches when selectedCompany changes and validate selectedBranch
  useEffect(() => {
    if (!selectedCompany) {
      setBranches([]);
      setBranchId("");
      return;
    }

    async function fetchBranches() {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/companies/${selectedCompany}/branches`
        );
        const branchData = res.data || [];
        setBranches(branchData);

        // Validate stored selectedBranch
        const storedBranch = localStorage.getItem("selectedBranch");
        if (storedBranch) {
          const parsedBranch = JSON.parse(storedBranch);
          const isValidBranch = branchData.some((b) => b.id === parsedBranch.id);
          if (!isValidBranch) {
            localStorage.removeItem("selectedBranch");
            setBranchId("");
          } else {
            setBranchId(parsedBranch.id);
          }
        }
      } catch (err) {
        console.error("fetchBranches", err);
        Swal.fire("Error", "Failed to load branches", "error");
        setBranchId("");
      }
    }
    fetchBranches();
  }, [selectedCompany]);

  // 🔹 Fetch bookings when selectedCompany or branchId changes
  useEffect(() => {
    if (selectedCompany) {
      fetchBookings(1);
    }
  }, [selectedCompany, branchId, search]);

  // 🔹 Fetch bookings
  const fetchBookings = async (pageNo = 1) => {
    if (!selectedCompany) {
      setBookings([]);
      setTotalPages(1);
      return;
    }
    setLoading(true);
    try {
      const params = {
        page: pageNo,
        pageSize,
        q: search || undefined,
        branch_id: branchId || undefined,
      };
      const res = await axios.get(
        `${API_BASE_URL}/companies/${selectedCompany}/bookings`,
        { params }
      );
      setBookings(res.data.bookings || []);
      setTotalPages(res.data.totalPages || 1);
      setPage(pageNo);
    } catch (err) {
      console.error("fetchBookings", err);
      Swal.fire("Error", "Failed to load bookings", "error");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Confirm booking
  const handleConfirm = async (bookingId) => {
    const confirm = await Swal.fire({
      title: "Confirm booking?",
      text: "This will change booking status from pending to confirmed.",
      icon: "question",
      showCancelButton: true,
    });
    if (!confirm.isConfirmed) return;

    try {
      await axios.post(`${API_BASE_URL}/bookings/${bookingId}/confirm`);
      Swal.fire("Confirmed", "Booking confirmed successfully", "success");
      fetchBookings(page);
    } catch (err) {
      console.error("confirm", err);
      Swal.fire("Error", "Failed to confirm booking", "error");
    }
  };

  // 🔹 Bill Modals
  const openCreateBillForBooking = (booking) => {
    setBillPrefill({ type: "fromBooking", booking });
    setShowBillModal(true);
  };

  const openManualBill = () => {
    if (!selectedCompany) {
      Swal.fire("Select Company", "Please select a company first", "info");
      return;
    }
    setBillPrefill({ type: "manual", companyId: selectedCompany });
    setShowBillModal(true);
  };

  const handleBillSaved = () => {
    setShowBillModal(false);
    fetchBookings(page);
    Swal.fire("Saved", "Bill created successfully", "success");
  };

  // 🔹 Phone / Email action handlers with themed confirmation modals
  const handlePhoneAction = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
    Swal.fire({
      title: "Call Customer?",
      html: `Do you want to call <strong>${phone}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Call Now",
      confirmButtonColor: "#0d6efd",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = `tel:${cleanPhone}`;
      }
    });
  };

  const handleEmailAction = (email) => {
    Swal.fire({
      title: "Send Email?",
      html: `Compose email to <strong>${email}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Open Email Client",
      confirmButtonColor: "#0d6efd",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = `mailto:${email}`;
      }
    });
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Manage Bookings — Upcoming & Today</h4>

      {/* 🔹 Company & Branch Selectors */}
      <Row className="mb-3 gy-3 align-items-end">
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Company</Form.Label>
            <Form.Select
              value={selectedCompany}
              onChange={(e) => {
                setSelectedCompany(e.target.value);
                setBranchId("");
                localStorage.removeItem("selectedBranch");
              }}
            >
              <option value="">Select Company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group>
            <Form.Label>Branch</Form.Label>
            <Form.Select
              value={branchId}
              onChange={(e) => {
                const selectedBranch = branches.find((b) => b.id === e.target.value);
                setBranchId(e.target.value);
                localStorage.setItem("selectedBranch", JSON.stringify(selectedBranch || {}));
              }}
              disabled={!branches.length}
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} ({b.city})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={4} className="text-md-end">
          <Button variant="primary" onClick={openManualBill}>
            <i className="bi bi-receipt me-2"></i>Create Manual Bill
          </Button>
        </Col>
      </Row>

      {/* 🔹 Search */}
      <Row className="mb-3">
        <Col xs={12} md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Search (customer / phone / service)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              variant="outline-primary"
              className="rounded-start-0"
              onClick={() => fetchBookings(1)}
            >
              <i className="bi bi-search"></i>
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {/* 🔹 Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Service(s)</th>
                  <th>Branch</th>
                  <th>Scheduled At</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-3">
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  bookings.map((b, i) => {
                    const svcList = (b.items || [])
                      .map(
                        (it) =>
                          `${it.service_name} (₹${it.price_cents.toFixed(2)})`
                      )
                      .join(", ");
                    const amount = b.total_amount_cents ?? 0;
                    return (
                      <tr key={b.id}>
                        <td>{(page - 1) * pageSize + i + 1}</td>
                        <td>{b.customer_name || "-"}</td>
                        <td>
                          {b.customer_phone && b.customer_phone !== "-" ? (
                            <a
                              href="#"
                              className="text-primary text-decoration-none"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePhoneAction(b.customer_phone);
                              }}
                              title="Click to call"
                            >
                              {b.customer_phone}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          {b.customer_email && b.customer_email !== "-" ? (
                            <a
                              href="#"
                              className="text-primary text-decoration-none"
                              onClick={(e) => {
                                e.preventDefault();
                                handleEmailAction(b.customer_email);
                              }}
                              title="Click to email"
                            >
                              {b.customer_email}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td style={{ minWidth: 220 }}>{svcList}</td>
                        <td>{b.branch_name || "All"}</td>
                        <td>
                          {b.next_scheduled_at
                            ? new Date(b.next_scheduled_at).toLocaleString()
                            : "-"}
                        </td>
                        <td>{b.status}</td>
                        <td>₹{amount.toFixed(2)}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end flex-wrap gap-1">
                            {b.status === "pending" && (
                              <Button
                                size="sm"
                                variant="success"
                                title="Confirm Booking"
                                onClick={() => handleConfirm(b.id)}
                              >
                                <i className="bi bi-check2-circle"></i>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="secondary"
                              title="Create Bill"
                              onClick={() => openCreateBillForBooking(b)}
                            >
                              <i className="bi bi-receipt"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <Pagination.Item
                  key={idx}
                  active={page === idx + 1}
                  onClick={() => fetchBookings(idx + 1)}
                >
                  {idx + 1}
                </Pagination.Item>
              ))}
            </Pagination>
          </div>
        </>
      )}

      {/* 🔹 Bill Modal */}
      <BillModal
        show={showBillModal}
        onHide={() => setShowBillModal(false)}
        prefill={billPrefill}
        onSaved={handleBillSaved}
      />
    </div>
  );
}